import React, { useState, useEffect } from 'react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { X, Lock, User, Search, AlertCircle } from 'lucide-react';
import { crearCitaManual } from '../../secretary/services/CalendarService';

interface PerfilPaciente {
  perfil_usuario_id: string;
  nombres_completos: string;
  apellidos_completos: string;
  avatar_url?: string;
}

export type EventoBottomSheetProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  selectedDate: Date;
  onEventCreated: () => void;
}>;

export function EventoBottomSheet({ visible, onClose, selectedDate, onEventCreated }: EventoBottomSheetProps) {
  const [tipoSeleccionado, setTipoSeleccionado] = useState<'ocupado' | 'cita_manual'>('ocupado');
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [horaFin, setHoraFin] = useState('10:00');
  const [nota, setNota] = useState('');

  // Estados específicos para cita manual
  const [pacienteMotivo, setPacienteMotivo] = useState('');
  const [montoCobrado, setMontoCobrado] = useState('');
  const [moneda, setMoneda] = useState<'USD' | 'EUR'>('USD');
  const [saving, setSaving] = useState(false);
  const [duracionMinutos, setDuracionMinutos] = useState<number>(45);

  // Nuevos estados para búsqueda (como en NuevaCitaSlideOver)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState<PerfilPaciente[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<PerfilPaciente | null>(null);

  const [nombres, setNombres] = useState('');
  const [telefono, setTelefono] = useState('');
  const [cedula, setCedula] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');



  useEffect(() => {
    if (visible) {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          supabase.from('profesional_config_agenda')
            .select('disponibilidad_jsonb')
            .eq('profesional_uuid', user.id)
            .single()
            .then(({ data }) => {
              if (data?.disponibilidad_jsonb) {
                try {
                  const parsed = typeof data.disponibilidad_jsonb === 'string' ? JSON.parse(data.disponibilidad_jsonb) : data.disponibilidad_jsonb;
                  if (parsed && Array.isArray(parsed) && parsed.length > 0 && parsed[0].duracion_minutos) {
                    setDuracionMinutos(parsed[0].duracion_minutos);
                  }
                } catch (e) {
                  console.error('Error parsing disponibilidad:', e);
                }
              }
            });
        }
      });
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  // Lógica de búsqueda de paciente
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 3) {
      setSearchResults([]);
      return;
    }
    let isMounted = true;
    const fetchPacientes = async () => {
      setIsSearching(true);
      const { data, error } = await supabase
        .from('perfiles_usuario')
        .select('perfil_usuario_id, nombres_completos, apellidos_completos, perfil_dni, avatar_url')
        .ilike('perfil_dni', `%${debouncedSearch}%`)
        .limit(5);
        
      if (isMounted) {
        if (!error && data) {
          setSearchResults(data);
        }
        setIsSearching(false);
      }
    };
    fetchPacientes();
    return () => { isMounted = false; };
  }, [debouncedSearch]);

  const handleSelectPaciente = (p: PerfilPaciente) => {
    setSelectedPaciente(p);
    setSearchQuery(`${p.nombres_completos} ${p.apellidos_completos}`);
    setCedula('');
    setTelefono('');
    setFechaNacimiento('');
    setSearchResults([]);
  };

  const handleClearPaciente = () => {
    setSelectedPaciente(null);
    setSearchQuery('');
    setCedula('');
    setTelefono('');
    setFechaNacimiento('');
  };  const handleHoraInicioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = e.target.value;
    setHoraInicio(newStart);
    
    if (!newStart) return;
    const [h, m] = newStart.split(':').map(Number);
    const dateObj = new Date();
    dateObj.setHours(h, m, 0, 0);
    dateObj.setMinutes(dateObj.getMinutes() + duracionMinutos);
    
    const endH = String(dateObj.getHours()).padStart(2, '0');
    const endM = String(dateObj.getMinutes()).padStart(2, '0');
    setHoraFin(`${endH}:${endM}`);
  };


  const saveCitaManual = async (userId: string, dateStr: string) => {
    const fechaInicioIso = new Date(`${dateStr}T${horaInicio}:00`).toISOString();
    const fechaFinIso = new Date(`${dateStr}T${horaFin}:00`).toISOString();

    const { success, message } = await crearCitaManual({
      fechaInicioIso,
      fechaFinIso,
      profesionalUuid: userId,
      pacienteUuid: selectedPaciente ? selectedPaciente.perfil_usuario_id : undefined,
      nuevoPacienteNombre: nombres || searchQuery,
      nuevoPacienteTelefono: telefono,
      nuevoPacienteCedula: cedula,
      nuevoPacienteFechaNacimiento: fechaNacimiento || undefined,
      motivo: pacienteMotivo || undefined,
      montoCobrado: montoCobrado.trim() !== '' ? Number.parseFloat(montoCobrado) : undefined,
      moneda,
      notaAdicional: nota.trim() === '' ? undefined : nota,
      activeWorkspaceType: 'personal' // Asumimos personal para este componente directamente
    });

    if (!success) throw new Error(message || 'Error al guardar cita manual');
  };

  const saveOcupado = async (userId: string, dateStr: string) => {
    const { error } = await supabase
      .from('agenda_eventos')
      .insert({
        profesional_uuid: userId,
        fecha: dateStr,
        inicio: `${horaInicio}:00`,
        fin: `${horaFin}:00`,
        tipo: tipoSeleccionado,
        nota: nota.trim() === '' ? null : nota
      });
    if (error) throw error;
  };

  const handleSave = async () => {
    if (horaInicio >= horaFin) {
      globalThis.alert('Error: La hora de fin debe ser posterior a la hora de inicio.');
      return;
    }

    if (tipoSeleccionado === 'cita_manual' && !selectedPaciente && (!searchQuery || !cedula)) {
      globalThis.alert('Error: Debes buscar un paciente registrado o ingresar la cédula para uno nuevo.');
      return;
    }

    setSaving(true);
    try {
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (!user || authErr) throw new Error('Usuario no autenticado');

      const dateStr = selectedDate.toISOString().split('T')[0];

      if (tipoSeleccionado === 'cita_manual') {
        await saveCitaManual(user.id, dateStr);
      } else {
        await saveOcupado(user.id, dateStr);
      }

      // Limpiar formulario y cerrar
      setTipoSeleccionado('ocupado');
      setNota('');
      setSearchQuery('');
      setNombres('');
      setCedula('');
      setTelefono('');
      setFechaNacimiento('');
      setSelectedPaciente(null);
      setPacienteMotivo('');
      setMontoCobrado('');
      onEventCreated();
      onClose();

    } catch (e: any) {
      console.error(e);
      globalThis.alert('Error: Hubo un problema al guardar el evento: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const renderDropdownContent = () => {
    if (isSearching) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#64748B', fontSize: '14px', fontWeight: 500 }}>
          Buscando paciente...
        </div>
      );
    }
    
    if (searchResults.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {searchResults.map(p => (
            <button
              type="button"
              key={p.perfil_usuario_id}
              onClick={() => handleSelectPaciente(p)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'none', border: 'none', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: '32px', height: '32px', backgroundColor: '#DBEAFE', color: '#2563EB', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', flexShrink: 0, overflow: 'hidden' }}>
                {p.avatar_url ? (
                  <img src={p.avatar_url} alt={p.nombres_completos} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  p.nombres_completos.charAt(0).toUpperCase()
                )}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.nombres_completos} {p.apellidos_completos}
                </span>
                <span style={{ display: 'block', fontSize: '12px', color: '#64748B' }}>Paciente Registrado</span>
              </div>
            </button>
          ))}
        </div>
      );
    }

    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#64748B', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '40px', height: '40px', backgroundColor: '#F1F5F9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertCircle size={20} color="#94A3B8" />
        </div>
        <p style={{ margin: 0, fontWeight: 500, color: '#334155' }}>No se encontraron pacientes</p>
        <p style={{ margin: 0, fontSize: '12px' }}>Completa los datos para registrarlo en el sistema.</p>
      </div>
    );
  };

  if (!visible) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
      zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor: '#FFFFFF', width: '100%', maxWidth: '500px',
        borderRadius: '24px 24px 0 0', padding: 0,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.1)'
      }}>
        {/* Handle visual */}
        <div style={{ width: '40px', height: '4px', backgroundColor: '#CBD5E1', borderRadius: '2px', margin: '12px auto' }} />

        <div style={{ padding: '0 20px 20px', overflowY: 'auto', flex: 1 }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', margin: 0 }}>Añadir Evento</h2>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={24} color="#64748B" />
            </button>
          </header>

          <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '20px', textTransform: 'capitalize' }}>
            Para el {selectedDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>

          {/* Selector de Tipo */}
          <div style={{ display: 'flex', backgroundColor: '#F1F5F9', borderRadius: '12px', padding: '4px', marginBottom: '24px' }}>
            <button
              type="button"
              onClick={() => setTipoSeleccionado('ocupado')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                padding: '10px 0', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: tipoSeleccionado === 'ocupado' ? '#3B82F6' : 'transparent',
                color: tipoSeleccionado === 'ocupado' ? '#FFFFFF' : '#64748B',
                fontWeight: 600, transition: 'all 0.2s', boxShadow: tipoSeleccionado === 'ocupado' ? '0 2px 4px rgba(59,130,246,0.2)' : 'none'
              }}
            >
              <Lock size={16} />
              Ocupado / Bloqueo
            </button>
            
            <button
              type="button"
              onClick={() => setTipoSeleccionado('cita_manual')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                padding: '10px 0', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: tipoSeleccionado === 'cita_manual' ? '#10B981' : 'transparent',
                color: tipoSeleccionado === 'cita_manual' ? '#FFFFFF' : '#64748B',
                fontWeight: 600, transition: 'all 0.2s', boxShadow: tipoSeleccionado === 'cita_manual' ? '0 2px 4px rgba(16,185,129,0.2)' : 'none'
              }}
            >
              <User size={16} />
              Cita Manual
            </button>
          </div>

          {/* Rango de Horas */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="hora-inicio" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Inicio</label>
              <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '0 8px' }}>
                <input 
                  id="hora-inicio" 
                  type="time" 
                  value={horaInicio} 
                  onChange={handleHoraInicioChange} 
                  style={{ width: '100%', height: '48px', border: 'none', background: 'transparent', outline: 'none', color: '#0F172A', fontSize: '15px', padding: '0 8px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <label htmlFor="hora-fin" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Fin</label>
              <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '0 8px' }}>
                <input 
                  id="hora-fin" 
                  type="time" 
                  value={horaFin} 
                  onChange={e => setHoraFin(e.target.value)} 
                  style={{ width: '100%', height: '48px', border: 'none', background: 'transparent', outline: 'none', color: '#0F172A', fontSize: '15px', padding: '0 8px', boxSizing: 'border-box', textAlign: 'center' }}
                />
              </div>
            </div>
          </div>

          {/* Formulario Dinámico */}
          {tipoSeleccionado === 'cita_manual' && (
            <div style={{ backgroundColor: '#ECFDF5', padding: '16px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #D1FAE5' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#065F46', margin: '0 0 16px 0' }}>Datos del Paciente</h3>
              
              <label htmlFor="paciente-buscar" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                Búsqueda por Cédula o Nombre
              </label>
              
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <Search style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748B' }} size={20} />
                <input 
                  id="paciente-buscar"
                  type="text"
                  placeholder="Ej. 12345678" 
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    if (selectedPaciente) setSelectedPaciente(null);
                  }}
                  readOnly={selectedPaciente !== null}
                  style={{ width: '100%', padding: '12px 16px 12px 48px', backgroundColor: selectedPaciente ? '#F8FAFC' : '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box' }}
                />
                {selectedPaciente && (
                  <button type="button" onClick={handleClearPaciente} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748B" />
                  </button>
                )}

                {/* Dropdown de Búsqueda */}
                {!selectedPaciente && (searchQuery.length >= 3 || isSearching) && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px', backgroundColor: '#FFF', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', border: '1px solid #E2E8F0', zIndex: 50, overflow: 'hidden' }}>
                    {renderDropdownContent()}
                  </div>
                )}
              </div>

              {/* Formulario Paciente Nuevo */}
              {!selectedPaciente && searchQuery.length >= 3 && searchResults.length === 0 && !isSearching && (
                <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ backgroundColor: '#FEF3C7', padding: '4px', borderRadius: '8px' }}>
                      <User size={16} color="#D97706" />
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#92400E' }}>Registrar Nuevo Paciente</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ position: 'relative' }}>
                      <input 
                        type="text" 
                        placeholder="Nombres y Apellidos" 
                        value={nombres} 
                        onChange={e => setNombres(e.target.value)} 
                        style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #FCD34D', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input 
                          type="text" 
                          placeholder="Cédula" 
                          value={cedula} 
                          onChange={e => setCedula(e.target.value)} 
                          style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #FCD34D', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input 
                          type="tel" 
                          placeholder="Teléfono" 
                          value={telefono} 
                          onChange={e => setTelefono(e.target.value)} 
                          style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #FCD34D', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <label htmlFor="fecha-nacimiento" style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#92400E', marginBottom: '4px' }}>Fecha de Nacimiento</label>
                      <input 
                        id="fecha-nacimiento"
                        type="date" 
                        value={fechaNacimiento} 
                        onChange={e => setFechaNacimiento(e.target.value)} 
                        style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #FCD34D', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box', color: '#475569' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <label htmlFor="paciente-motivo" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Motivo de consulta (Opcional)</label>
              <input 
                id="paciente-motivo"
                type="text"
                placeholder="Ej. Control de rutina" 
                value={pacienteMotivo}
                onChange={e => setPacienteMotivo(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', fontSize: '15px', marginBottom: '16px', boxSizing: 'border-box' }}
              />

              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#065F46', margin: '16px 0' }}>Registro de Cobro</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="monto-cobrado" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Monto cobrado</label>
                  <input 
                    id="monto-cobrado"
                    type="number"
                    step="0.01"
                    placeholder="0.00" 
                    value={montoCobrado}
                    onChange={e => setMontoCobrado(e.target.value)}
                    style={{ width: '100%', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', fontSize: '15px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label htmlFor="moneda-select" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Moneda</label>
                  <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '0 8px' }}>
                    <select id="moneda-select" value={moneda} onChange={(e) => setMoneda(e.target.value as any)} style={{ width: '100%', height: '44px', border: 'none', background: 'transparent', outline: 'none', fontSize: '15px' }}>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#059669', lineHeight: '18px', marginTop: '12px' }}>Nota: Este cobro no pasa por la plataforma digital, es solo para tu registro contable interno.</p>
            </div>
          )}

          <label htmlFor="nota-adicional" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Nota adicional (Opcional)</label>
          <textarea 
            id="nota-adicional"
            placeholder={tipoSeleccionado === 'ocupado' ? 'Ej. Almuerzo, Reunión de clínica...' : 'Observaciones de la consulta...'}
            value={nota}
            onChange={e => setNota(e.target.value)}
            style={{ width: '100%', height: '80px', padding: '12px 16px', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', fontSize: '15px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
          />

          {/* Footer Actions */}
          <div style={{ paddingTop: '16px', borderTop: '1px solid #F1F5F9', marginTop: '24px' }}>
            <button 
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                width: '100%', backgroundColor: '#0F172A', color: '#FFFFFF', fontSize: '16px', fontWeight: 700,
                padding: '16px 0', borderRadius: '14px', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Guardando...' : 'Guardar Evento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
